import { useCallback, useEffect } from 'react';
import { core_v1_DataSource } from '@antimatterhq/asl-api-ts-v1';
import { produce, WritableDraft } from 'immer';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@antimatterhq/ui/components/accordion';
import { Button } from '@antimatterhq/ui/components/button';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Text } from '@antimatterhq/ui/components/typography';
import { PresetHeading } from '../../_partials/PresetHeading';
import {
  GoldReview,
  SourceSelectionReview,
  PreTransformReview,
  BronzeReview,
  SilverReview,
} from '@/components/dataSources/review/';
import { cn } from '@/lib/utils';
import {
  StepId,
  useConfigDataSource,
} from '@/contexts/ConfigDataSourceContext';
import { TwoColumnLayout } from '@/components/container';
import { TocWrapper } from '@/components/tableOfContents/ToCWrapper';
import { TableOfContentsProvider } from '@/contexts/TableOfContentContext';
import { TableOfContents } from '@/components/tableOfContents/TableOfContents';
import {
  clearEnableOnComplete,
  isCompleted,
  setCompleted,
  shouldEnableOnComplete,
} from '@/lib/datasource/uiStatus';
import { ResponseError } from '@/components/responseError/ResponseError';

export type DataSourceReviewProps = {
  dataSource: core_v1_DataSource;
  handleGoToStep: (stepId: StepId) => void;
  overrideEnabled?: boolean;
  save: (dataSource: core_v1_DataSource) => Promise<void>;
  isSaving: boolean;
};

const sections = [
  {
    step: 'getting-started',
    title: 'Source selection',
    cssClass: '',
  },
  {
    step: 'bronze',
    title: 'Bronze table',
    cssClass: 'border-bronze',
  },
  {
    step: 'silver-pre-transform',
    title: 'Silver pre-transform',
    cssClass: 'border-silver',
  },
  {
    step: 'silver',
    title: 'Silver',
    cssClass: 'border-silver',
  },
  {
    step: 'gold',
    title: 'Gold',
    cssClass: 'border-gold',
  },
] as const;

export default function DataSourceReview() {
  const {
    dataSource,
    save,
    isSaving,
    handleGoToStep,
    publishChanges,
    responseError,
    setFormik,
  } = useConfigDataSource();

  const enableOnComplete = shouldEnableOnComplete(dataSource);
  const isCreate = !isCompleted(dataSource);
  useEffect(() => {
    setFormik(undefined);
  }, [setFormik]);

  const onPublish = useCallback(() => {
    publishChanges();
  }, [publishChanges]);

  const onCreate = useCallback(() => {
    let data = dataSource;
    if (enableOnComplete) {
      data = produce(
        dataSource,
        (draftState: WritableDraft<core_v1_DataSource>) => {
          draftState.spec.schedule!.enabled = true;
        },
      );
    }
    data = clearEnableOnComplete(data);
    data = setCompleted(data);
    save(data);
  }, [save, dataSource, enableOnComplete]);

  const Review = (
    <div className="w-full" data-testid="review-datasource-page">
      <Accordion
        type="multiple"
        className="w-full"
        defaultValue={sections.map(({ step }) => step)}
        data-testid="review-accordion"
      >
        {sections.map(({ step, title, cssClass }) => (
          <TocWrapper key={step} displayName={title} name={step}>
            <AccordionItem
              value={step}
              key={step}
              className={cn(
                'border-b-0',
                cssClass,
                cssClass && 'border-t-[3px]',
              )}
              data-testid={`review-section-${step}`}
            >
              <AccordionTrigger data-testid={`review-${step}`}>
                <Text
                  size="lg"
                  className="font-bold"
                  data-testid={`review-title-${step}`}
                >
                  {title}
                </Text>
              </AccordionTrigger>
              <AccordionContent data-testid={`review-content-${step}`}>
                <div
                  className="relative"
                  data-testid={`review-content-container-${step}`}
                >
                  <div
                    className="absolute right-0 top-0"
                    data-testid={`review-edit-container-${step}`}
                  >
                    <Button
                      variant="tertiary"
                      iconOnly
                      leftIcon="antimatterhq:pencil"
                      onClick={() => handleGoToStep(step)}
                      data-testid={`review-edit-button-${step}`}
                    />
                  </div>
                  {step === 'getting-started' && (
                    <SourceSelectionReview dataSource={dataSource} />
                  )}
                  {step === 'bronze' && (
                    <BronzeReview dataSource={dataSource} />
                  )}
                  {step === 'silver-pre-transform' && (
                    <PreTransformReview dataSource={dataSource} />
                  )}
                  {step === 'silver' && (
                    <SilverReview dataSource={dataSource} />
                  )}
                  {step === 'gold' && <GoldReview dataSource={dataSource} />}
                </div>
              </AccordionContent>
            </AccordionItem>
          </TocWrapper>
        ))}
      </Accordion>

      <Spacer size="lg" />

      <ResponseError
        error={responseError}
        data-testid="review-response-error"
      />

      <div
        className="flex justify-between"
        data-testid="review-navigation-container"
      >
        <Button
          type="button"
          variant="default"
          leftIcon="lucide:chevron-left"
          onClick={() => handleGoToStep('gold')}
          disabled={isSaving}
          data-testid="review-back-button"
        >
          Back
        </Button>
        {isCreate && (
          <Button
            type="button"
            variant="primary"
            onClick={() => onCreate()}
            disabled={isSaving}
            data-testid="review-create-button"
            loading={isSaving}
          >
            Create new datasource
          </Button>
        )}
        {!isCreate && (
          <Button
            type="button"
            variant="primary"
            onClick={() => onPublish()}
            disabled={isSaving}
            data-testid="review-apply-button"
            loading={isSaving}
          >
            Apply changes
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <TableOfContentsProvider>
      <Spacer size="lg" />
      <TwoColumnLayout.Root data-testid="review-layout-root">
        <TwoColumnLayout.LeftColumn>
          <PresetHeading />
          {Review}
        </TwoColumnLayout.LeftColumn>
        <TwoColumnLayout.RightColumn>
          <TableOfContents data-testid="review-table-of-contents" />
        </TwoColumnLayout.RightColumn>
      </TwoColumnLayout.Root>
    </TableOfContentsProvider>
  );
}
